import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, ArrowRight, Tag, Facebook, Instagram, Linkedin } from 'lucide-react';
import PageBanner from '../components/PageBanner';
import Footer from '../components/Footer';
import { getMembers, getCategories } from '../lib/api';
import { IMAGES } from '../mock';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const TILE_COLORS = ['#3d7ca6', '#4a9cd4', '#5a8f6b', '#8a7a5c', '#6b7f99', '#7a9c8f'];
function tileColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return TILE_COLORS[Math.abs(h) % TILE_COLORS.length];
}

function MemberCard({ m }) {
  return (
    <Link
      to={`/directory/${m.id}`}
      data-testid={`member-card-${m.id}`}
      className="group bg-white rounded-sm overflow-hidden border border-neutral-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.10)] hover:-translate-y-1 transition-all duration-400 flex flex-col"
    >
      {m.image ? (
        <div className="relative h-44 overflow-hidden">
          <img
            src={m.image}
            alt={m.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 flex items-center gap-3 px-5 pb-4">
            <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 shadow" style={{ background: tileColor(m.name) }}>
              <span className="font-serif-ah text-white text-[20px]">{m.name.charAt(0)}</span>
            </div>
            <h3 className="font-serif-ah text-white text-[20px] leading-tight line-clamp-2 drop-shadow">{m.name}</h3>
          </div>
        </div>
      ) : (
        <div className="h-24 flex items-center px-6 gap-4" style={{ background: tileColor(m.name) }}>
          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center shrink-0">
            <span className="font-serif-ah text-white text-[26px]">{m.name.charAt(0)}</span>
          </div>
          <h3 className="font-serif-ah text-white text-[21px] leading-tight line-clamp-2">{m.name}</h3>
        </div>
      )}
      <div className="p-6 flex-1 flex flex-col">
        {m.city && (
          <p className="flex items-center gap-2 font-sans-ah text-[13px] text-[#666] mb-3">
            <MapPin size={15} className="text-[#4a9cd4]" /> {m.city}
          </p>
        )}
        <p className="font-sans-ah text-[14px] text-[#555] font-300 leading-relaxed line-clamp-3 flex-1">
          {m.description || 'A proud member of the Almaguin Highlands Chamber of Commerce.'}
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          {(m.categories || []).slice(0, 2).map((c) => (
            <span key={c} className="font-sans-ah text-[10px] tracking-wide-ah uppercase text-[#3d7ca6] bg-[#eef4f8] px-2.5 py-1 rounded-sm">
              {c}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between mt-5">
          <span className="inline-flex items-center gap-2 font-sans-ah text-[12px] tracking-wide-ah text-[#4a9cd4] group-hover:gap-3 transition-all">
            VIEW PROFILE <ArrowRight size={14} />
          </span>
          {(m.facebook || m.instagram || m.linkedin) && (
            <div className="flex items-center gap-2" data-testid={`card-socials-${m.id}`}>
              {[['facebook', m.facebook, Facebook], ['instagram', m.instagram, Instagram], ['linkedin', m.linkedin, Linkedin]].map(([key, url, Icon]) =>
                url ? (
                  <button
                    key={key}
                    type="button"
                    aria-label={key}
                    data-testid={`card-social-${key}-${m.id}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(url, '_blank', 'noopener'); }}
                    className="w-7 h-7 rounded-full border border-neutral-200 flex items-center justify-center text-[#7a94a6] hover:bg-[#4a9cd4] hover:text-white hover:border-[#4a9cd4] transition-colors"
                  >
                    <Icon size={13} />
                  </button>
                ) : null
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function DirectoryPage() {
  const [members, setMembers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== 'all') params.category = category;
    getMembers(params)
      .then(setMembers)
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, [category]);

  const filtered = useMemo(() => {
    if (!search.trim()) return members;
    const q = search.toLowerCase();
    return members.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        (m.description || '').toLowerCase().includes(q) ||
        (m.city || '').toLowerCase().includes(q) ||
        (m.categories || []).some((c) => c.toLowerCase().includes(q))
    );
  }, [members, search]);

  return (
    <>
      <PageBanner
        title="Business Directory"
        crumb="DIRECTORY"
        subtitle="Discover Almaguin's finest businesses — search our local member directory to find who you need in the region."
        image={IMAGES.heroB}
      />

      <section className="bg-[#f8f6f2] py-14 md:py-20">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          {/* Controls */}
          <div className="flex flex-col md:flex-row gap-4 mb-10">
            <div className="relative flex-1">
              <Search size={19} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9aa]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, city or keyword..."
                className="w-full h-14 pl-12 pr-4 bg-white border border-neutral-200 rounded-sm font-sans-ah text-[15px] focus:outline-none focus:border-[#4a9cd4] transition-colors"
              />
            </div>
            <div className="md:w-[320px]">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-14 bg-white border-neutral-200 rounded-sm font-sans-ah text-[15px]">
                  <div className="flex items-center gap-2">
                    <Tag size={16} className="text-[#4a9cd4]" />
                    <SelectValue placeholder="All Categories" />
                  </div>
                </SelectTrigger>
                <SelectContent className="max-h-[320px]">
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.name} value={c.name}>
                      {c.name} ({c.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <p className="font-sans-ah text-[14px] text-[#777] mb-8">
            {loading ? 'Loading members...' : `${filtered.length} member${filtered.length === 1 ? '' : 's'} found`}
          </p>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
              {['sk-a', 'sk-b', 'sk-c', 'sk-d', 'sk-e', 'sk-f'].map((k) => (
                <div key={k} className="h-[300px] bg-white rounded-sm animate-pulse border border-neutral-100" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24">
              <p className="font-serif-ah text-[26px] text-[#555]">No members match your search.</p>
              <button onClick={() => { setSearch(''); setCategory('all'); }} className="mt-4 font-sans-ah text-[13px] tracking-wide-ah text-[#4a9cd4]">
                CLEAR FILTERS
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
              {filtered.map((m) => <MemberCard key={m.id} m={m} />)}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
}
